import * as React from 'react';

import {
  SelectionItem,
  useSelectionGroup,
} from '@/ux-common/hooks/use-selection-group/use-selection-group';

import { TabGroupContent } from './tab-group-content';
import { TabGroupProps, TabRenderProps } from './types';

/**
 * TabGroup — headless selection manager for a row of Tab triggers.
 *
 * TabGroup owns state, layout, and the row's ARIA/keyboard behaviour; the
 * consumer owns rendering. Pass `items` (the set of tabs) and a render function
 * that returns a Tab element for each. Supports controlled (`selectedIds`) and
 * uncontrolled (`initialSelectedIds`) modes, single- and multi-select, and
 * scrollable overflow.
 *
 * Spread `tabProps` onto each rendered tab. It carries `role="tab"`,
 * `aria-selected`, and the roving-tabindex wiring — without it the group
 * renders `role="tablist"` around children that are not tabs.
 *
 * Unrecognised props are forwarded to the `role="tablist"` element, which is
 * how you give the row an accessible name and a `data-component`.
 *
 * @example
 * ```tsx
 * const items = [
 *   { id: 'overview', data: { label: 'Overview' } },
 *   { id: 'activity', data: { label: 'Activity' } },
 * ];
 *
 * <TabGroup
 *   items={items}
 *   initialSelectedIds={['overview']}
 *   onSelectionChange={(selected) => console.log(selected)}
 *   aria-label="Views"
 * >
 *   {({ tabs }) =>
 *     tabs.map(({ tab, state, onToggle, disabled, tabProps }) => (
 *       <Tab
 *         key={tab.id}
 *         {...tabProps}
 *         data-state={state}
 *         onClick={onToggle}
 *         disabled={disabled}
 *       >
 *         {tab.data.label}
 *       </Tab>
 *     ))
 *   }
 * </TabGroup>
 * ```
 */
export function TabGroup<T>({
  items,
  children,
  initialSelectedIds,
  selectedIds,
  multiselect = false,
  onSelectionChange,
  className,
  disabled = false,
  scrollable = false,
  withScrollShadow = false,
  ...tablistProps
}: TabGroupProps<T>) {
  const { isSelected, toggleSelection } = useSelectionGroup({
    items,
    initialSelectedIds,
    selectedIds,
    multiselect,
    onSelectionChange,
    disabled,
  });

  /* Keyed by item id rather than index so reordering or splicing `items` never
     points a stale ref at the wrong tab. */
  const tabNodes = React.useRef(new Map<string, HTMLElement>());
  const [rovingId, setRovingId] = React.useState<string | null>(null);

  const isTabDisabled = React.useCallback(
    (item: SelectionItem<T>) => disabled || item.disabled || false,
    [disabled]
  );

  /* Which tab is the row's single tab stop.

     Until the user moves focus it follows selection, per APG — tabbing into a
     tab row should land on the tab that is currently active, not always on the
     first one. After that the user's own position wins. Falling back to the
     first enabled tab covers a group with nothing selected yet.

     Every candidate is checked for being enabled before it is accepted, and
     that check is the whole point rather than a formality. A disabled tab
     cannot take focus, so making it the row's only tab stop leaves the group
     unreachable from the keyboard entirely — every other tab holds -1. That
     state is reachable three ways: `initialSelectedIds` naming a disabled
     item, a controlled `selectedIds` containing one, or an item going disabled
     while it stays selected across a refetch. It is also a regression this
     component would introduce rather than inherit: before roving tabindex,
     each tab was a natively tabbable button, so a disabled selection cost the
     user nothing.

     `rovingId` gets the same treatment for the same reason — the tab the user
     last focused can be disabled by a refetch after the fact.

     -1 when every tab is disabled, meaning no tab stop at all. There is
     nothing focusable to point at, and pointing at a disabled tab anyway is
     worse than pointing at nothing: inert for a `<button>`, and actively wrong
     for a consumer who renders something else and gets a focusable element
     they marked disabled. */
  /* One pass rather than three findIndex scans. Disabled items are skipped
     outright, so "is this candidate enabled" is structural here rather than a
     check bolted onto each branch.

     Not memoised, deliberately. A correct dependency list would have to include
     selection, and in uncontrolled mode selection is internal state inside
     useSelectionGroup with no prop to keys off — `selectedIds` is undefined
     there. A memo keyed on `selectedIds` would silently stop tracking selection
     for every uncontrolled consumer, which is a real bug traded for a scan of
     an array the row can barely fit twenty of. */
  let fromIdIndex = -1;
  let selectedIndex = -1;
  let firstEnabledIndex = -1;

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];

    if (isTabDisabled(item)) {
      continue;
    }

    if (firstEnabledIndex < 0) {
      firstEnabledIndex = index;
    }
    if (rovingId && item.id === rovingId) {
      fromIdIndex = index;
    }
    if (selectedIndex < 0 && isSelected(item.id)) {
      selectedIndex = index;
    }
  }

  const rovingIndex =
    fromIdIndex >= 0 ? fromIdIndex : selectedIndex >= 0 ? selectedIndex : firstEnabledIndex;

  /**
   * Moves focus to the next enabled tab, wrapping at both ends.
   *
   * Disabled tabs are skipped rather than focused — a disabled `<button>` is
   * not focusable anyway, so stopping on one would strand the user. The loop is
   * bounded by `items.length`, so an all-disabled row simply does nothing.
   */
  const moveRoving = React.useCallback(
    (fromIndex: number, step: 1 | -1) => {
      const count = items.length;

      for (let offset = 1; offset <= count; offset += 1) {
        const index = (((fromIndex + step * offset) % count) + count) % count;
        const candidate = items[index];

        if (isTabDisabled(candidate)) {
          continue;
        }

        setRovingId(candidate.id);
        tabNodes.current.get(candidate.id)?.focus();
        return;
      }
    },
    [items, isTabDisabled]
  );

  /* Manual activation: arrows move focus only, and Enter/Space activate through
     the element's own click handling. Automatic activation (selecting on
     arrow) is the other APG option, but it cannot express multi-select and
     would change what every existing consumer's arrow keys do.

     One handler for the whole row rather than a factory producing one per tab.
     The originating index comes from `currentTarget` — the element `tabProps`
     was spread onto — instead of being closed over, which is what lets a single
     function serve every tab. `currentTarget` rather than `target` because a
     tab commonly wraps an icon or a badge, and those are what `target` reports.

     Kept on `tabProps` rather than delegated to the tablist. Delegation would
     work — events bubble — but the tablist's props are spread consumer-last, so
     a consumer passing their own `onKeyDown` would silently replace this one and
     take roving tabindex with it. No error, no failing test. */
  const handleTabKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const index = items.findIndex(
        (item) => tabNodes.current.get(item.id) === event.currentTarget
      );

      if (index < 0) {
        return;
      }

      switch (event.key) {
        case 'ArrowRight':
          moveRoving(index, 1);
          break;
        case 'ArrowLeft':
          moveRoving(index, -1);
          break;
        case 'Home':
          moveRoving(-1, 1);
          break;
        case 'End':
          moveRoving(0, -1);
          break;
        default:
          return;
      }

      event.preventDefault();
    },
    [items, moveRoving]
  );

  /* Same shape as the key handler: one function for the row, origin read off
     the event rather than closed over. */
  const handleTabFocus = React.useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      const focused = items.find((item) => tabNodes.current.get(item.id) === event.currentTarget);

      if (focused) {
        setRovingId(focused.id);
      }
    },
    [items]
  );

  /* Ref callbacks are memoised per tab id, not rebuilt per render.

     React compares ref callback identity: hand it a new arrow each render and it
     detaches and re-attaches every tab's ref every time, even when nothing about
     the row changed. Hoisting the *body* into a stable function does not fix
     that — `(node) => register(tab.id, node)` is still a fresh closure. The
     identity has to be the thing that persists, so the callbacks are cached.

     The entry deletes itself on unmount, which is also what keeps the cache from
     growing without bound as `items` churns. */
  const tabRefs = React.useRef(new Map<string, (node: HTMLElement | null) => void>());

  const getTabRef = (id: string) => {
    const cached = tabRefs.current.get(id);

    if (cached) {
      return cached;
    }

    const ref = (node: HTMLElement | null) => {
      if (node) {
        tabNodes.current.set(id, node);
      } else {
        tabNodes.current.delete(id);
        tabRefs.current.delete(id);
      }
    };

    tabRefs.current.set(id, ref);
    return ref;
  };

  const tabs: TabRenderProps<T>[] = items.map((tab: SelectionItem<T>, index: number) => ({
    tab,
    state: isSelected(tab.id) ? 'active' : 'inactive',
    onToggle: () => toggleSelection(tab),
    disabled: isTabDisabled(tab),
    tabProps: {
      role: 'tab',
      'aria-selected': isSelected(tab.id),
      tabIndex: index === rovingIndex ? 0 : -1,
      ref: getTabRef(tab.id),
      onKeyDown: handleTabKeyDown,
      onFocus: handleTabFocus,
    },
  }));

  return (
    <TabGroupContent
      scrollable={scrollable}
      className={className}
      withScrollShadow={withScrollShadow}
      tablistProps={{
        'data-component': 'TabGroup',
        // Only meaningful when the row actually allows more than one selection.
        'aria-multiselectable': multiselect || undefined,
        ...tablistProps,
      }}
    >
      {children({ tabs })}
    </TabGroupContent>
  );
}
